import { libraryRequest, libraryResponse, libraryReadTicket } from '../utils/librarySync.js';
import { resilientFetch as fetch, coalesceWrite } from '../utils/resilientFetch.js';
import { analyticsHeaders } from '../utils/analytics.js';
// TMDB API для фильмов и сериалов
const TMDB_API_KEY = 'c00d5258867ca3cbb5b363ed8a6a89bc';
const TMDB_BASE_URL = 'https://api.themoviedb.org/3';
const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w500';

// Google Books API для книг (запросы идут через серверный прокси /api/external/googlebooks/*)

// iTunes API для музыки
const ITUNES_BASE_URL = 'https://itunes.apple.com/search';

// Сервис для проксирования изображений (для России)
const PROXY_IMAGE_URL = 'https://wsrv.nl/?url=';

// Backend API (должно быть до всех функций, использующих этот URL)
const BACKEND_URL = '/api';

// VAPID public key для Push-уведомлений (должен совпадать с backend)
const VAPID_PUBLIC_KEY = 'BDH_YTvQ0QPZTB4Cy7IMXWraHTT2W6FksdmXmvJqnaph4msJ8rgfSnMQoEQTb2G3XwXaDVIvFYKguUwxI9eEfSo';

// ===== User Collections API =====

export const getUserCollections = async () => {
  return fetchWithAuth('/collections');
};

export const createCollection = async (name) => {
  return fetchWithAuth('/collections', {
    method: 'POST',
    body: JSON.stringify({ name })
  });
};

export const updateCollection = async (id, data) => {
  return fetchWithAuth(`/collections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const deleteCollection = async (id) => {
  return fetchWithAuth(`/collections/${id}`, {
    method: 'DELETE'
  });
};

export const getCollectionItems = async (id) => {
  return fetchWithAuth(`/collections/${id}/items`);
};

export const addItemToCollection = async (collectionId, shelfItemId) => {
  return fetchWithAuth(`/collections/${collectionId}/items`, {
    method: 'POST',
    body: JSON.stringify({ shelf_item_id: shelfItemId })
  });
};

export const addItemsToCollectionBatch = async (collectionId, shelfItemIds) => {
  return fetchWithAuth(`/collections/${collectionId}/items/batch`, {
    method: 'POST',
    body: JSON.stringify({ shelf_item_ids: shelfItemIds })
  });
};

export const removeItemFromCollection = async (collectionId, shelfItemId) => {
  return fetchWithAuth(`/collections/${collectionId}/items/${shelfItemId}`, {
    method: 'DELETE'
  });
};

export const shareCollection = async (collectionId, friendId) => {
  return fetchWithAuth(`/collections/${collectionId}/share`, {
    method: 'POST',
    body: JSON.stringify({ friend_id: friendId })
  });
};

export const addCollectionToShelf = async (collectionId) => {
  return fetchWithAuth(`/collections/${collectionId}/add-to-shelf`, {
    method: 'POST'
  });
};

export const getSharedCollection = async (collectionId) => {
  return fetchWithAuth(`/collections/${collectionId}/shared`);
};

// ==== Notifications API ====

export const getNotifications = async () => {
  const result = [], cursors = new Set();
  let cursor = null;
  do {
    const params = new URLSearchParams({cursor_mode:'1',grouped:'1',limit:'100'});
    if(cursor) params.set('cursor',cursor);
    const data = await fetchWithAuth(`/notifications?${params}`);
    result.push(...data.notifications);
    cursor = data.next_cursor;
    if(cursor && cursors.has(cursor)) throw new Error('Не удалось загрузить следующую страницу уведомлений');
    if(cursor) cursors.add(cursor);
  } while(cursor);
  return Array.from(new Map(result.map(row => [row.id, row])).values());
};
export const muteNotificationThread = (notificationId, muted) => fetchWithAuth('/notifications/mute-thread', {method:'POST', body:JSON.stringify({notification_id:notificationId,muted})});

export const getUnreadNotificationCount = async () => {
  return fetchWithAuth('/notifications/unread-count');
};

export const markNotificationAsRead = async (notificationId) => {
  return fetchWithAuth(`/notifications/${notificationId}/read`, {
    body: JSON.stringify({grouped:true}),
    method: 'PUT'
  });
};

export const markAllNotificationsAsRead = async () => {
  return fetchWithAuth('/notifications/read-all', {
    method: 'PUT'
  });
};

export const sendSharedProjectNotification = async (recipientId, itemId, itemCategory, itemTitle, itemImage) => {
  return fetchWithAuth('/notifications/share-project', {
    method: 'POST',
    body: JSON.stringify({ recipientId, itemId, itemCategory, itemTitle, itemImage })
  });
};

export const sendSharedCollectionNotification = async (recipientId, collectionId, collectionName) => {
  return fetchWithAuth('/notifications/share-collection', {
    method: 'POST',
    body: JSON.stringify({ recipientId, collectionId, collectionName })
  });
};

export const copyCollectionToMyCollections = async (collectionId) => {
  return fetchWithAuth(`/collections/${collectionId}/copy-to-my-collections`, {
    method: 'POST'
  });
};

// ===== Shared Collections API =====

export const getSharedCollections = async () => {
  return fetchWithAuth('/shared-collections');
};

export const getPublicSharedCollections = async () => {
  return fetchWithAuth('/shared-collections/public');
};

export const createSharedCollection = async (data) => {
  return fetchWithAuth('/shared-collections', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const getSharedCollectionById = async (id) => {
  return fetchWithAuth(`/shared-collections/${id}`);
};

export const updateSharedCollection = async (id, data) => {
  return fetchWithAuth(`/shared-collections/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const deleteSharedCollection = async (id) => {
  return fetchWithAuth(`/shared-collections/${id}`, {
    method: 'DELETE'
  });
};

export const inviteToSharedCollection = async (id, data) => {
  return fetchWithAuth(`/shared-collections/${id}/invite`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const respondToSharedCollectionInvite = async (id, action) => {
  return fetchWithAuth(`/shared-collections/${id}/respond`, {
    method: 'POST',
    body: JSON.stringify({ action })
  });
};

export const setSharedCollectionModerator = async (id, userId) => {
  return fetchWithAuth(`/shared-collections/${id}/moderator`, {
    method: 'POST',
    body: JSON.stringify({ user_id: userId })
  });
};

export const removeSharedCollectionMember = async (id, userId) => {
  return fetchWithAuth(`/shared-collections/${id}/members/${userId}`, {
    method: 'DELETE'
  });
};

export const leaveSharedCollection = async (id) => {
  return fetchWithAuth(`/shared-collections/${id}/leave`, {
    method: 'POST'
  });
};

export const getSharedCollectionItems = async (id) => {
  return fetchWithAuth(`/shared-collections/${id}/items`);
};

export const addItemToSharedCollection = async (id, data) => {
  return fetchWithAuth(`/shared-collections/${id}/items`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const removeItemFromSharedCollection = async (id, itemId) => {
  return fetchWithAuth(`/shared-collections/${id}/items/${itemId}`, {
    method: 'DELETE'
  });
};

export const voteOnSharedCollectionItem = async (id, itemId, vote) => {
  return fetchWithAuth(`/shared-collections/${id}/items/${itemId}/vote`, {
    method: 'POST',
    body: JSON.stringify({ vote })
  });
};

export const getSharedCollectionComments = async (id, itemId) => {
  const params = itemId ? `?item_id=${itemId}` : '';
  return fetchWithAuth(`/shared-collections/${id}/comments${params}`);
};

export const addSharedCollectionComment = async (id, data) => {
  return fetchWithAuth(`/shared-collections/${id}/comments`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const getSharedCollectionActivity = async (id, limit = 50) => {
  return fetchWithAuth(`/shared-collections/${id}/activity?limit=${limit}`);
};

export const joinSharedCollection = async (id) => {
  return fetchWithAuth(`/shared-collections/${id}/join`, {
    method: 'POST'
  });
};

// ===== Episodes API =====

export const getSeasonEpisodes = async (showId, seasonNumber) => {
  try {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    const response = await fetch(`${BACKEND_URL}/episodes/${showId}/season/${seasonNumber}`, { headers });
    if (!response.ok) {
      console.error('Season fetch failed:', response.status);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching season episodes:', error);
    return null;
  }
};

export const markEpisodeWatched = async (showId, seasonNumber, episodeNumber, watched) => {
  try {
    return await fetchWithAuth('/episodes/watched', {
      method: 'POST',
      body: JSON.stringify({ show_id: showId, season_number: seasonNumber, episode_number: episodeNumber, watched })
    });
  } catch (error) {
    console.error('Error marking episode watched:', error);
    throw error;
  }
};

export const rateEpisode = async (showId, seasonNumber, episodeNumber, rating) => {
  try {
    return await fetchWithAuth('/episodes/rating', {
      method: 'POST',
      body: JSON.stringify({ show_id: showId, season_number: seasonNumber, episode_number: episodeNumber, rating })
    });
  } catch (error) {
    console.error('Error rating episode:', error);
    throw error;
  }
};

export const getEpisodeProgress = async (showId) => {
  try {
    return await fetchWithAuth(`/episodes/${showId}/progress`);
  } catch (error) {
    console.error('Error fetching episode progress:', error);
    return { progress: {} };
  }
};

// Надёжная загрузка файла на сервер через XMLHttpRequest
export const uploadFile = (file) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('polka_token');
    if (!token) { reject(new Error('Не найден токен авторизации')); return; }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_URL}/upload`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data.url) resolve(data.url);
          else reject(new Error('Пустой ответ сервера'));
        } catch { reject(new Error('Неверный ответ сервера')); }
      } else {
        let msg = 'Ошибка загрузки';
        try { const d = JSON.parse(xhr.responseText); msg = d.error || msg; } catch { /* ignore */ }
        reject(new Error(`${msg} (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Ошибка сети'));
    xhr.ontimeout = () => reject(new Error('Таймаут загрузки'));
    xhr.timeout = 120000;
    xhr.send(formData);
  });
};

export const subscribeToSharedCollection = async (id) => {
  return fetchWithAuth(`/shared-collections/${id}/subscribe`, {
    method: 'POST'
  });
};

export const getUserSharedCollections = async (userId) => {
  return fetchWithAuth(`/shared-collections/user/${userId}`);
};

// RAWG может быть недоступен (например, сетевые блокировки). Чтобы не спамить ошибками
// и не подвешивать UI повторными запросами, ставим "cooldown" на повторные попытки.
let rawgUnavailableUntil = 0;
const RAWG_COOLDOWN_MS = 5 * 60 * 1000;

const isRawgUnavailable = () => Date.now() < rawgUnavailableUntil;
const IGDB_IMAGE_BASE = 'https://images.igdb.com/igdb/image/upload';
export const igdbImageUrl = (imageId, size = 'cover_big') => {
  if (!imageId) return null;
  return proxyImageUrl(`${IGDB_IMAGE_BASE}/t_${size}/${imageId}.jpg`);
};

export const igdbToGameCard = (g) => {
  if (!g) return null;
  const poster = g.cover?.image_id ? igdbImageUrl(g.cover.image_id, 'cover_big') : null;
  const screenshot0 = g.screenshots?.[0]?.image_id
    ? igdbImageUrl(g.screenshots[0].image_id, 'screenshot_big')
    : null;

  const released = g.first_release_date
    ? new Date(g.first_release_date * 1000).toISOString().split('T')[0]
    : null;

  const rating = typeof g.aggregated_rating === 'number' ? g.aggregated_rating / 20 : null; // 0..5-ish
  const metacritic = typeof g.aggregated_rating === 'number' ? Math.round(g.aggregated_rating) : null; // 0..100-ish

  return {
    id: g.id,
    rawgId: g.id, // совместимость со старым полем, чтобы не ломать существующий код
    igdbId: g.id,
    title: g.name,
    slug: g.slug,
    released,
    rating,
    ratingsCount: g.aggregated_rating_count,
    metacritic,
    poster,
    background: screenshot0 || poster,
    platforms: g.platforms?.map(p => p.name).filter(Boolean) || [],
    genres: g.genres?.map(ge => ge.name).filter(Boolean) || [],
    category: 'game'
  };
};

// Функция для проксирования изображений TMDB
export const proxyImageUrl = (url) => {
  if (!url) return '';
  return `${PROXY_IMAGE_URL}${encodeURIComponent(url)}`;
};

// TMDB - список альтернативных обложек (постеров) и фонов для фильма/сериала
export const getTMDBImages = async (id, category) => {
  try {
    if (!id || isNaN(id) || id <= 0) return { posters: [], backdrops: [] };
    const type = category === 'series' ? 'tv' : 'movie';
    const response = await fetch(`${BACKEND_URL}/external/tmdb/${type}/${id}/images`);
    if (!response.ok) {
      console.error('TMDB images request failed:', response.status);
      return { posters: [], backdrops: [] };
    }
    const data = await response.json();
    return {
      posters: (data.posters || [])
        .filter(p => p.file_path)
        .slice(0, 30)
        .map(p => proxyImageUrl(`${TMDB_IMAGE_BASE}${p.file_path}`)),
      backdrops: (data.backdrops || [])
        .filter(b => b.file_path)
        .slice(0, 30)
        .map(b => proxyImageUrl(`https://image.tmdb.org/t/p/w780${b.file_path}`))
    };
  } catch (error) {
    console.error('Error getting TMDB images:', error);
    return { posters: [], backdrops: [] };
  }
};

// TMDB - поиск фильмов (через бэкенд-прокси для обхода CORS)
export const searchMovies = async (query, page = 1) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/tmdb/search/movie?query=${encodeURIComponent(query)}&page=${page}`
    );
    if (!response.ok) {
      throw new Error(`TMDB movie search proxy error: ${response.status}`);
    }
    const data = await response.json();
    return {
      results: data.results.map(movie => ({
        id: movie.id,
        tmdbId: movie.id,
        title: movie.title,
        originalTitle: movie.original_title,
        overview: movie.overview,
        releaseDate: movie.release_date,
        rating: movie.vote_average,
        poster: movie.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${movie.poster_path}`) : null,
        backdrop: movie.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${movie.backdrop_path}`) : null,
        genres: [],
        popularity: movie.popularity,
        category: 'film'
      })),
      totalPages: data.total_pages,
      totalResults: data.total_results
    };
  } catch (error) {
    console.error('Error searching movies:', error);
    throw error;
  }
};

// TMDB - поиск сериалов (через бэкенд-прокси для обхода CORS)
export const searchTVShows = async (query, page = 1) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/tmdb/search/tv?query=${encodeURIComponent(query)}&page=${page}`
    );
    if (!response.ok) {
      throw new Error(`TMDB TV search proxy error: ${response.status}`);
    }
    const data = await response.json();
    return {
      results: data.results.map(show => ({
        id: show.id,
        tmdbId: show.id,
        title: show.name,
        originalTitle: show.original_name,
        overview: show.overview,
        releaseDate: show.first_air_date,
        rating: show.vote_average,
        poster: show.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${show.poster_path}`) : null,
        backdrop: show.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${show.backdrop_path}`) : null,
        genres: [],
        popularity: show.popularity,
        category: 'series'
      })),
      totalPages: data.total_pages,
      totalResults: data.total_results
    };
  } catch (error) {
    console.error('Error searching TV shows:', error);
    throw error;
  }
};

// TMDB - получение деталей фильма (через бэкенд-прокси для обхода CORS)
export const getMovieDetails = async (movieId) => {
  try {
    // Проверяем, что movieId является валидным числом
    if (!movieId || isNaN(movieId) || movieId <= 0) {
      console.error('Invalid movie ID:', movieId);
      return null;
    }

    const [detailsResponse, imagesResponse] = await Promise.all([
      fetch(`${BACKEND_URL}/external/tmdb/movie/${movieId}`),
      fetch(`${BACKEND_URL}/external/tmdb/movie/${movieId}/images`)
    ]);
    
    // Проверяем статус ответа
    if (!detailsResponse.ok) {
      console.error('Movie not found:', movieId, detailsResponse.status);
      return null;
    }
    
    const data = await detailsResponse.json();
    
    // Проверяем, что данные существуют
    if (!data || data.success === false) {
      console.error('Invalid movie data:', movieId);
      return null;
    }
    
    const imagesData = await imagesResponse.json();
    
    // Получаем первый backdrop с размером w1280
    const backdrop = imagesData.backdrops?.[0]?.file_path 
      ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${imagesData.backdrops[0].file_path}`)
      : (data.backdrop_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${data.backdrop_path}`) : null);
    
    // Получаем режиссера из credits
    const director = data.credits?.crew?.find(person => person.job === 'Director');
    
    return {
      id: data.id,
      title: data.title,
      originalTitle: data.original_title,
      overview: data.overview,
      releaseDate: data.release_date,
      rating: data.vote_average || 0,
      voteCount: data.vote_count || 0,
      runtime: data.runtime || 0,
      poster: data.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${data.poster_path}`) : null,
      backdrop: backdrop,
      genres: data.genres || [],
      budget: data.budget || 0,
      revenue: data.revenue || 0,
      status: data.status,
      tagline: data.tagline,
      original_language: data.original_language,
      spoken_languages: data.spoken_languages || [],
      director: director ? {
        id: director.id,
        name: director.name,
        profile: director.profile_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${director.profile_path}`) : null
      } : null,
      cast: data.credits?.cast?.slice(0, 10).map(actor => ({
        id: actor.id,
        name: actor.name,
        character: actor.character,
        profile: actor.profile_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${actor.profile_path}`) : null
      })) || [],
      videos: data.videos?.results?.filter(video => video.type === 'Trailer').slice(0, 3) || [],
      category: 'film'
    };
  } catch (error) {
    console.error('Error getting movie details:', error);
    return null;
  }
};

// TMDB - получение деталей сериала
export const getTVShowDetails = async (showId) => {
  try {
    // Проверяем, что showId является валидным числом
    if (!showId || isNaN(showId) || showId <= 0) {
      console.error('Invalid show ID:', showId);
      return null;
    }

    const [detailsResponse, imagesResponse] = await Promise.all([
      fetch(`${BACKEND_URL}/external/tmdb/tv/${showId}`),
      fetch(`${BACKEND_URL}/external/tmdb/tv/${showId}/images`)
    ]);
    
    // Проверяем статус ответа
    if (!detailsResponse.ok) {
      console.error('TV show not found:', showId, detailsResponse.status);
      return null;
    }
    
    const data = await detailsResponse.json();
    
    // Проверяем, что данные существуют
    if (!data || data.success === false) {
      console.error('Invalid TV show data:', showId);
      return null;
    }
    
    const imagesData = await imagesResponse.json();
    
    // Получаем первый backdrop с размером w1280
    const backdrop = imagesData.backdrops?.[0]?.file_path 
      ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${imagesData.backdrops[0].file_path}`)
      : (data.backdrop_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${data.backdrop_path}`) : null);
    
    return {
      id: data.id,
      title: data.name,
      originalTitle: data.original_name,
      overview: data.overview,
      releaseDate: data.first_air_date,
      lastAirDate: data.last_air_date,
      rating: data.vote_average || 0,
      voteCount: data.vote_count || 0,
      numberOfSeasons: data.number_of_seasons || 0,
      numberOfEpisodes: data.number_of_episodes || 0,
      runtime: Array.isArray(data.episode_run_time) && data.episode_run_time.length ? data.episode_run_time[0] : 0,
      episode_run_time: data.episode_run_time || [],
      poster: data.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${data.poster_path}`) : null,
      backdrop: backdrop,
      genres: data.genres || [],
      status: data.status,
      tagline: data.tagline,
      original_language: data.original_language,
      spoken_languages: data.spoken_languages || [],
      networks: data.networks || [],
      creator: data.created_by?.[0] ? {
        id: data.created_by[0].id,
        name: data.created_by[0].name,
        profile: data.created_by[0].profile_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${data.created_by[0].profile_path}`) : null
      } : null,
      cast: data.credits?.cast?.slice(0, 10).map(actor => ({
        id: actor.id,
        name: actor.name,
        character: actor.character,
        profile: actor.profile_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${actor.profile_path}`) : null
      })) || [],
      videos: data.videos?.results?.filter(video => video.type === 'Trailer').slice(0, 3) || [],
      seasons: data.seasons?.map(season => ({
        id: season.id,
        name: season.name,
        overview: season.overview,
        seasonNumber: season.season_number,
        episodeCount: season.episode_count,
        poster: season.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${season.poster_path}`) : null
      })) || [],
      category: 'series'
    };
  } catch (error) {
    console.error('Error getting TV show details:', error);
    return null;
  }
};

export const getMovieRecommendations = async (movieId, limit = 10) => {
  try {
    if (!movieId || isNaN(movieId) || movieId <= 0) {
      console.error('Invalid movie ID for recommendations:', movieId);
      return [];
    }

    const response = await fetch(`${BACKEND_URL}/external/tmdb/movie/${movieId}/recommendations`);
    if (!response.ok) {
      console.error('Movie recommendations not found:', movieId, response.status);
      return [];
    }

    const data = await response.json();
    return (data.results || []).slice(0, limit).map(item => ({
      id: item.id,
      title: item.title || item.name || item.original_title || item.original_name || '',
      poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
      backdrop: item.backdrop_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.backdrop_path}`) : null,
      releaseDate: item.release_date || item.first_air_date,
      category: 'film'
    }));
  } catch (error) {
    console.error('Error getting movie recommendations:', error);
    return [];
  }
};

export const getTVShowRecommendations = async (showId, limit = 10) => {
  try {
    if (!showId || isNaN(showId) || showId <= 0) {
      console.error('Invalid TV show ID for recommendations:', showId);
      return [];
    }

    const response = await fetch(`${BACKEND_URL}/external/tmdb/tv/${showId}/recommendations`);
    if (!response.ok) {
      console.error('TV show recommendations not found:', showId, response.status);
      return [];
    }

    const data = await response.json();
    return (data.results || []).slice(0, limit).map(item => ({
      id: item.id,
      title: item.name || item.title || item.original_name || item.original_title || '',
      poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
      backdrop: item.backdrop_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.backdrop_path}`) : null,
      releaseDate: item.first_air_date || item.release_date,
      category: 'series'
    }));
  } catch (error) {
    console.error('Error getting TV show recommendations:', error);
    return [];
  }
};

export const getTVShowSimilar = async (showId, limit = 10) => {
  try {
    if (!showId || isNaN(showId) || showId <= 0) {
      return [];
    }
    const response = await fetch(`${BACKEND_URL}/external/tmdb/tv/${showId}/similar`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return (data.results || []).slice(0, limit).map(item => ({
      id: item.id,
      title: item.name || item.title || item.original_name || item.original_title || '',
      poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
      backdrop: item.backdrop_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.backdrop_path}`) : null,
      releaseDate: item.first_air_date || item.release_date,
      category: 'series'
    }));
  } catch (error) {
    console.error('Error getting TV show similar:', error);
    return [];
  }
};

// IGDB - поиск игр (через бэкенд-прокси)
export const searchGames = async (query, page = 1) => {
  if (isRawgUnavailable()) {
    const fallback = await fetchWithAuth(`/feed/search/shelves?q=${encodeURIComponent(query)}&category=game`);
    const uniqueByExternalId = new Map();
    (fallback || []).forEach((item) => {
      const key = `${item.category}-${item.external_id}`;
      if (!uniqueByExternalId.has(key)) {
        uniqueByExternalId.set(key, {
          id: item.external_id,
          rawgId: item.external_id,
          title: item.title,
          slug: item.title?.toLowerCase().replace(/\s+/g, '-'),
          released: item.release_date,
          rating: item.rating,
          poster: item.poster || item.backdrop || item.item_image || null,
          category: 'game'
        });
      }
    });
    const results = Array.from(uniqueByExternalId.values());
    return {
      results,
      totalPages: 1,
      totalResults: results.length
    };
  }

  try {
    const response = await fetch(
      `${BACKEND_URL}/external/igdb/games?search=${encodeURIComponent(query)}&page=${page}&page_size=20`
    );
    if (!response.ok) {
      throw new Error(`IGDB proxy error: ${response.status}`);
    }
    const data = await response.json();
    return {
      results: (data.results || []).map(igdbToGameCard).filter(Boolean),
      totalPages: page, // IGDB через этот прокси не даёт total count надёжно
      totalResults: data.count ?? (data.results || []).length
    };
  } catch {
    // Fallback: локальный поиск по полкам пользователей
    const fallback = await fetchWithAuth(`/feed/search/shelves?q=${encodeURIComponent(query)}&category=game`);
    const uniqueByExternalId = new Map();
    (fallback || []).forEach((item) => {
      const key = `${item.category}-${item.external_id}`;
      if (!uniqueByExternalId.has(key)) {
        uniqueByExternalId.set(key, {
          id: item.external_id,
          rawgId: item.external_id,
          title: item.title,
          slug: item.title?.toLowerCase().replace(/\s+/g, '-'),
          released: item.release_date,
          rating: item.rating,
          poster: item.poster || item.backdrop || item.item_image || null,
          category: 'game'
        });
      }
    });
    const results = Array.from(uniqueByExternalId.values());
    return {
      results,
      totalPages: 1,
      totalResults: results.length
    };
  }
};

// IGDB - получение деталей игры
export const getGameDetails = async (gameId) => {
  try {
    const [detailsResponse, timeToBeatResponse] = await Promise.all([
      fetch(`${BACKEND_URL}/external/igdb/games/${gameId}`),
      fetch(`${BACKEND_URL}/external/igdb/games/${gameId}/time-to-beat`)
    ]);
    if (!detailsResponse.ok) {
      throw new Error(`IGDB proxy error: ${detailsResponse.status}`);
    }
    const data = await detailsResponse.json();
    if (!data) return null;
    const timeToBeatRaw = timeToBeatResponse.ok ? await timeToBeatResponse.json() : [];

    const screenshots = (data.screenshots || [])
      .map((s) => {
        const url = s?.image_id ? igdbImageUrl(s.image_id, 'screenshot_big') : null;
        if (!url) return null;
        return { id: s.id || s.image_id, image: url };
      })
      .filter(Boolean);

    const poster = data.cover?.image_id ? igdbImageUrl(data.cover.image_id, 'cover_big') : null;
    const released = data.first_release_date
      ? new Date(data.first_release_date * 1000).toISOString().split('T')[0]
      : null;

    const aggregatedRating = typeof data.aggregated_rating === 'number' ? data.aggregated_rating : null;
    const similarGames = (data.similar_games || [])
      .map((game) => ({
        id: game?.id,
        title: game?.name || '',
        poster: game?.cover?.image_id ? igdbImageUrl(game.cover.image_id, 'cover_big') : null,
        category: 'game'
      }))
      .filter((game) => game.id && game.title)
      .slice(0, 10);

    const preferTime = (timeType) => (timeToBeatRaw || []).find((row) => row?.time_type === timeType) || null;
    const ttbMain = preferTime(0) || (timeToBeatRaw || [])[0] || null;
    const ttbNormally = ttbMain?.normally || null;
    const ttbHastily = ttbMain?.hastily || null;
    const ttbCompletely = ttbMain?.completely || null;

    return {
      id: data.id,
      title: data.name,
      slug: data.slug,
      description: data.storyline || data.summary,
      released,
      rating: aggregatedRating !== null ? aggregatedRating / 20 : null,
      ratingsCount: data.aggregated_rating_count,
      metacritic: aggregatedRating !== null ? Math.round(aggregatedRating) : null,
      poster,
      background: screenshots[0]?.image || poster,
      website: data.websites?.find(w => w.category === 1)?.url || data.websites?.[0]?.url,
      genres: data.genres || [],
      developers: (data.involved_companies || [])
        .filter(ic => ic?.developer)
        .map(ic => ic.company)
        .filter(Boolean),
      publishers: (data.involved_companies || [])
        .filter(ic => ic?.publisher)
        .map(ic => ic.company)
        .filter(Boolean),
      platforms: data.platforms || [],
      screenshots,
      similarGames,
      playtime: ttbNormally,
      playtimeHastily: ttbHastily,
      playtimeCompletely: ttbCompletely,
      category: 'game'
    };
  } catch {
    return null;
  }
};

// IGDB - альтернативные обложки игры (арты + скриншоты)
export const getGameImages = async (gameId) => {
  try {
    if (!gameId) return { posters: [], backdrops: [] };
    const response = await fetch(`${BACKEND_URL}/external/igdb/games/${gameId}`);
    if (!response.ok) {
      console.error('IGDB images request failed:', response.status);
      return { posters: [], backdrops: [] };
    }
    const data = await response.json();
    const toUrl = (img) => (img?.image_id ? igdbImageUrl(img.image_id, 'screenshot_big') : null);
    return {
      posters: (data.artworks || []).map(toUrl).filter(Boolean).slice(0, 20),
      backdrops: (data.screenshots || []).map(toUrl).filter(Boolean).slice(0, 20)
    };
  } catch (error) {
    console.error('Error getting IGDB images:', error);
    return { posters: [], backdrops: [] };
  }
};

export const getGameSimilar = async (gameId, limit = 10) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/igdb/games/${gameId}/similar?limit=${encodeURIComponent(limit)}`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return (data || []).slice(0, limit).map((game) => ({
      id: game.id,
      title: game.name || '',
      poster: game?.cover?.image_id ? igdbImageUrl(game.cover.image_id, 'cover_big') : null,
      releaseDate: game.first_release_date ? new Date(game.first_release_date * 1000).toISOString().split('T')[0] : null,
      category: 'game'
    }));
  } catch (error) {
    console.error('Error getting game similar:', error);
    return [];
  }
};

export const getSimilarByCategory = async (category, itemId, limit = 10) => {
  if (!category || !itemId) return [];
  if (category === 'film') return getMovieRecommendations(itemId, limit);
  if (category === 'series') return getTVShowRecommendations(itemId, limit);
  if (category === 'game') return getGameSimilar(itemId, limit);
  return [];
};

// iTunes - lookup по ID (через бэкенд-прокси для обхода CORS)
export const lookupMusic = async (id, country = 'RU') => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/itunes/lookup?id=${encodeURIComponent(id)}&country=${encodeURIComponent(country)}`
    );
    if (!response.ok) {
      throw new Error(`iTunes proxy error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error looking up music:', error);
    return null;
  }
};

// iTunes - поиск музыки (через бэкенд-прокси для обхода CORS)
export const searchMusic = async (query, page = 1) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/itunes/search?term=${encodeURIComponent(query)}&page=${page}&limit=20&country=RU`
    );
    if (!response.ok) {
      throw new Error(`iTunes search proxy error: ${response.status}`);
    }
    const data = await response.json();
    return {
      results: data.results.map(item => ({
        id: item.trackId || item.collectionId,
        itunesId: item.trackId || item.collectionId,
        title: item.trackName || item.collectionName,
        artistName: item.artistName,
        collectionName: item.collectionName,
        artwork: item.artworkUrl100 ? proxyImageUrl(item.artworkUrl100.replace('100x100', '600x600')) : null,
        previewUrl: item.previewUrl,
        trackTimeMillis: item.trackTimeMillis,
        releaseDate: item.releaseDate,
        primaryGenreName: item.primaryGenreName,
        country: item.country,
        currency: item.currency,
        trackPrice: item.trackPrice,
        collectionPrice: item.collectionPrice,
        trackViewUrl: item.trackViewUrl,
        collectionViewUrl: item.collectionViewUrl,
        kind: item.kind,
        wrapperType: item.wrapperType,
        category: 'music'
      })),
      totalResults: data.resultCount
    };
  } catch (error) {
    console.error('Error searching music:', error);
    throw error;
  }
};

// Поиск обложки книги через iTunes (entity=ebook) для улучшения качества
const findItunesBookCover = async (title, authors = []) => {
  try {
    // Формируем поисковый запрос: название + первый автор
    const searchTerm = [title, ...authors].filter(Boolean).join(' ');
    const response = await fetch(
      `${BACKEND_URL}/external/itunes/search/ebooks?term=${encodeURIComponent(searchTerm)}&country=US`
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (!data.results?.length) return null;

    // Ищем наиболее подходящий результат: совпадение по автору
    const authorLower = authors[0]?.toLowerCase() || '';
    let bestMatch = data.results[0];
    if (authorLower) {
      const match = data.results.find(r =>
        r.artistName?.toLowerCase().includes(authorLower)
      );
      if (match) bestMatch = match;
    }

    // Берём artworkUrl600 (заменяем 100x100 на 600x600)
    const artwork = bestMatch.artworkUrl100?.replace('100x100', '600x600');
    if (!artwork) return null;

    return proxyImageUrl(artwork);
  } catch (error) {
    console.error('Error finding iTunes book cover:', error);
    return null;
  }
};

// Очистка описания от HTML-тегов
const stripHtmlTags = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Google Books - детали книги
export const getBookDetails = async (bookId) => {
  try {
    // Идём через серверный прокси (обход 503/CORS и защита API-ключа)
    const response = await fetch(
      `${BACKEND_URL}/external/googlebooks/${encodeURIComponent(bookId)}`
    );
    if (!response.ok) {
      throw new Error(`Google Books proxy error: ${response.status}`);
    }
    const data = await response.json();
    const info = data.volumeInfo || {};

    // Пытаемся найти обложку лучшего качества через iTunes
    const itunesCover = await findItunesBookCover(info.title, info.authors);

    return {
      id: data.id,
      googleId: data.id,
      title: info.title,
      subtitle: info.subtitle,
      authors: info.authors || [],
      publisher: info.publisher,
      publishedDate: info.publishedDate,
      description: stripHtmlTags(info.description),
      pageCount: info.pageCount,
      categories: info.categories || [],
      averageRating: info.averageRating,
      ratingsCount: info.ratingsCount,
      language: info.language,
      previewLink: info.previewLink,
      infoLink: info.infoLink,
      poster: itunesCover || (info.imageLinks?.thumbnail ? proxyImageUrl(info.imageLinks.thumbnail) : null),
      artwork: itunesCover || (info.imageLinks?.thumbnail ? proxyImageUrl(info.imageLinks.thumbnail.replace('http:', 'https:').replace('zoom=1', 'zoom=2')) : null),
      backdrop: null,
      isbn10: info.industryIdentifiers?.find(id => id.type === 'ISBN_10')?.identifier,
      isbn13: info.industryIdentifiers?.find(id => id.type === 'ISBN_13')?.identifier,
      maturityRating: info.maturityRating,
      printType: info.printType,
      category: 'book'
    };
  } catch (error) {
    console.error('Error getting book details:', error);
    return null;
  }
};

// Google Books - поиск книг
export const searchBooks = async (query, page = 1) => {
  try {
    // Идём через серверный прокси (обход 503/CORS и защита API-ключа)
    const response = await fetch(
      `${BACKEND_URL}/external/googlebooks/search?query=${encodeURIComponent(query)}&page=${page}`
    );
    if (!response.ok) {
      throw new Error(`Google Books search proxy error: ${response.status}`);
    }
    const data = await response.json();
    return {
      results: data.items?.map(item => ({
        id: item.id,
        googleId: item.id,
        title: item.volumeInfo.title,
        subtitle: item.volumeInfo.subtitle,
        authors: item.volumeInfo.authors || [],
        publisher: item.volumeInfo.publisher,
        publishedDate: item.volumeInfo.publishedDate,
        description: stripHtmlTags(item.volumeInfo.description),
        pageCount: item.volumeInfo.pageCount,
        categories: item.volumeInfo.categories || [],
        averageRating: item.volumeInfo.averageRating,
        ratingsCount: item.volumeInfo.ratingsCount,
        language: item.volumeInfo.language,
        previewLink: item.volumeInfo.previewLink,
        infoLink: item.volumeInfo.infoLink,
        poster: item.volumeInfo.imageLinks?.thumbnail ? proxyImageUrl(item.volumeInfo.imageLinks.thumbnail) : null,
        isbn10: item.volumeInfo.industryIdentifiers?.find(id => id.type === 'ISBN_10')?.identifier,
        isbn13: item.volumeInfo.industryIdentifiers?.find(id => id.type === 'ISBN_13')?.identifier,
        maturityRating: item.volumeInfo.maturityRating,
        printType: item.volumeInfo.printType,
        saleInfo: item.saleInfo,
        accessInfo: item.accessInfo,
        category: 'book'
      })) || [],
      totalResults: data.totalItems || 0
    };
  } catch (error) {
    console.error('Error searching books:', error);
    throw error;
  }
};

// Объединенный поиск по всем категориям
export const searchAll = async (query, onlyKeys = null) => {
  const providers = { movies: searchMovies, series: searchTVShows, games: searchGames, music: searchMusic, books: searchBooks };
  const keys = onlyKeys || Object.keys(providers);
  const responses = await Promise.allSettled(keys.map(key => providers[key](query)));
  if (!onlyKeys && responses.every(result => result.status === 'rejected')) throw new Error('Поиск временно недоступен. Повторите запрос.');
  const data = {}, totalResults = {}, failedCategories = [];
  responses.forEach((result, index) => {
    const key = keys[index];
    data[key] = result.status === 'fulfilled' ? result.value.results : [];
    totalResults[key] = result.status === 'fulfilled' ? result.value.totalResults : 0;
    if (result.status === 'rejected') failedCategories.push(key);
  });
  return { ...data, totalResults, failedCategories };
};

// Получение трендов
export const getTrending = async (mediaType = 'all', timeWindow = 'week') => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/tmdb/trending/${mediaType}/${timeWindow}`
    );
    const data = await response.json();
    return data.results.map(item => ({
      id: item.id,
      tmdbId: item.id,
      title: item.title || item.name,
      originalTitle: item.original_title || item.original_name,
      overview: item.overview,
      releaseDate: item.release_date || item.first_air_date,
      rating: item.vote_average,
      poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
      backdrop: item.backdrop_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.backdrop_path}`) : null,
      mediaType: item.media_type,
      category: item.media_type === 'movie' ? 'film' : item.media_type === 'tv' ? 'series' : item.media_type
    }));
  } catch (error) {
    console.error('Error getting trending:', error);
    return [];
  }
};

// TMDB - получение списка фильмов по ID списка
export const getTmdbList = async (listId, page = 1) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/tmdb/list/${listId}?page=${page}`);
    if (!response.ok) {
      throw new Error(`TMDB list proxy error: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching TMDB list:', error);
    return { items: [] };
  }
};

const CHUNK_SIZE = 5;

// TMDB - batch получение нескольких сериалов по списку ID (с разбивкой на чанки)
export const getTvBatch = async (ids) => {
  try {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) return [];

    const results = [];
    for (let i = 0; i < validIds.length; i += CHUNK_SIZE) {
      const chunk = validIds.slice(i, i + CHUNK_SIZE);
      const response = await fetch(`${BACKEND_URL}/external/tmdb/tv/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: chunk })
      });
      if (!response.ok) {
        throw new Error(`TMDB TV batch proxy error: ${response.status}`);
      }
      const chunkResults = await response.json();
      results.push(...chunkResults);
    }
    return results;
  } catch (error) {
    console.error('Error fetching TMDB TV batch:', error);
    return [];
  }
};

// TMDB - batch получение нескольких фильмов по списку ID (с разбивкой на чанки)
export const getMoviesBatch = async (ids) => {
  try {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) return [];

    const results = [];
    for (let i = 0; i < validIds.length; i += CHUNK_SIZE) {
      const chunk = validIds.slice(i, i + CHUNK_SIZE);
      const response = await fetch(`${BACKEND_URL}/external/tmdb/movies/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: chunk })
      });
      if (!response.ok) {
        throw new Error(`TMDB batch proxy error: ${response.status}`);
      }
      const chunkResults = await response.json();
      results.push(...chunkResults);
    }
    return results;
  } catch (error) {
    console.error('Error fetching TMDB batch:', error);
    return [];
  }
};

// Google Books - batch получение нескольких книг по списку ID
export const getBooksBatch = async (ids) => {
  try {
    const idsParam = ids.filter(Boolean).join(',');
    if (!idsParam) return [];
    const response = await fetch(`${BACKEND_URL}/external/googlebooks/batch?ids=${idsParam}`);
    if (!response.ok) {
      throw new Error(`Google Books batch proxy error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching Google Books batch:', error);
    return [];
  }
};

// IGDB - получение последних релизов игр (за последние 30 дней)
// IGDB - batch получение нескольких игр по списку ID
export const getCompanyDetails = async (companyId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/igdb/company/${companyId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Error getting company details:', error);
    return null;
  }
};

export const getCompanyGames = async (companyId, page = 1) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/igdb/company/${companyId}/games?page=${page}`);
    if (!response.ok) return { results: [], count: 0 };
    return await response.json();
  } catch (error) {
    console.error('Error getting company games:', error);
    return { results: [], count: 0 };
  }
};

export const getGamesBatch = async (ids) => {
  try {
    const idsParam = ids.filter(Boolean).join(',');
    if (!idsParam) return [];
    const response = await fetch(`${BACKEND_URL}/external/igdb/games/batch?ids=${idsParam}`);
    if (!response.ok) {
      throw new Error(`IGDB batch proxy error: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching IGDB batch:', error);
    return [];
  }
};

export const getRecentGameReleases = async (pageSize = 5) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/igdb/games?search=&page=1&page_size=${pageSize}&recent=true`
    );
    if (!response.ok) {
      throw new Error(`IGDB proxy error: ${response.status}`);
    }
    const data = await response.json();
    return (data.results || []).map(igdbToGameCard).filter(Boolean);
  } catch {
    return [];
  }
};

// TMDB - получение последних релизов фильмов
export const getRecentMovieReleases = async (pageSize = 5) => {
  try {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    const formatDate = (date) => date.toISOString().split('T')[0];
    const startDate = formatDate(thirtyDaysAgo);
    const endDate = formatDate(today);
    
    const response = await fetch(
      `${BACKEND_URL}/external/tmdb/discover/movie?sort_by=vote_average.desc&primary_release_date.gte=${startDate}&primary_release_date.lte=${endDate}&vote_count.gte=10&page_size=${pageSize}`
    );
    const data = await response.json();
    
    // Получаем детали для каждого фильма, включая backdrop
    const moviesWithDetails = await Promise.all(
      data.results.slice(0, pageSize).map(async (movie) => {
        try {
          const detailsResponse = await fetch(
            `${BACKEND_URL}/external/tmdb/movie/${movie.id}`
          );
          const details = await detailsResponse.json();
          
          return {
            id: movie.id,
            tmdbId: movie.id,
            title: movie.title,
            originalTitle: movie.original_title,
            overview: movie.overview,
            releaseDate: movie.release_date,
            rating: movie.vote_average,
            poster: movie.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${movie.poster_path}`) : null,
            backdrop: details.backdrop_path 
              ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${details.backdrop_path}`)
              : (movie.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${movie.backdrop_path}`) : null),
            category: 'film'
          };
        } catch {
          return {
            id: movie.id,
            tmdbId: movie.id,
            title: movie.title,
            originalTitle: movie.original_title,
            overview: movie.overview,
            releaseDate: movie.release_date,
            rating: movie.vote_average,
            poster: movie.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${movie.poster_path}`) : null,
            backdrop: movie.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${movie.backdrop_path}`) : null,
            category: 'film'
          };
        }
      })
    );
    
    return moviesWithDetails;
  } catch (error) {
    console.error('Error getting recent movie releases:', error);
    return [];
  }
};

// TMDB - получение последних релизов сериалов
export const getRecentTVShowReleases = async (pageSize = 5) => {
  try {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const formatDate = (date) => date.toISOString().split('T')[0];
    const startDate = formatDate(thirtyDaysAgo);
    const endDate = formatDate(today);

    const response = await fetch(
      `${BACKEND_URL}/external/tmdb/discover/tv?sort_by=vote_average.desc&first_air_date.gte=${startDate}&first_air_date.lte=${endDate}&vote_count.gte=10&page_size=${pageSize}`
    );
    const data = await response.json();

    // Получаем детали для каждого сериала, включая backdrop
    const showsWithDetails = await Promise.all(
      data.results.slice(0, pageSize).map(async (show) => {
        try {
          const detailsResponse = await fetch(
            `${BACKEND_URL}/external/tmdb/tv/${show.id}`
          );
          const details = await detailsResponse.json();

          return {
            id: show.id,
            tmdbId: show.id,
            title: show.name,
            originalTitle: show.original_name,
            overview: show.overview,
            releaseDate: show.first_air_date,
            rating: show.vote_average,
            poster: show.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${show.poster_path}`) : null,
            backdrop: details.backdrop_path
              ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${details.backdrop_path}`)
              : (show.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${show.backdrop_path}`) : null),
            category: 'series'
          };
        } catch {
          return {
            id: show.id,
            tmdbId: show.id,
            title: show.name,
            originalTitle: show.original_name,
            overview: show.overview,
            releaseDate: show.first_air_date,
            rating: show.vote_average,
            poster: show.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${show.poster_path}`) : null,
            backdrop: show.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${show.backdrop_path}`) : null,
            category: 'series'
          };
        }
      })
    );

    return showsWithDetails;
  } catch (error) {
    console.error('Error getting recent TV show releases:', error);
    return [];
  }
};

// TMDB - получение популярных фильмов
export const getPopularMovies = async (pageSize = 5) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/tmdb/movie/popular?page=1`
    );
    const data = await response.json();

    // Получаем детали для каждого фильма, включая backdrop
    const moviesWithDetails = await Promise.all(
      data.results.slice(0, pageSize).map(async (movie) => {
        try {
          const detailsResponse = await fetch(
            `${BACKEND_URL}/external/tmdb/movie/${movie.id}`
          );
          const details = await detailsResponse.json();

          return {
            id: movie.id,
            tmdbId: movie.id,
            title: movie.title,
            originalTitle: movie.original_title,
            overview: movie.overview,
            releaseDate: movie.release_date,
            rating: movie.vote_average,
            poster: movie.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${movie.poster_path}`) : null,
            backdrop: details.backdrop_path
              ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${details.backdrop_path}`)
              : (movie.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${movie.backdrop_path}`) : null),
            category: 'film'
          };
        } catch {
          return {
            id: movie.id,
            tmdbId: movie.id,
            title: movie.title,
            originalTitle: movie.original_title,
            overview: movie.overview,
            releaseDate: movie.release_date,
            rating: movie.vote_average,
            poster: movie.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${movie.poster_path}`) : null,
            backdrop: movie.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${movie.backdrop_path}`) : null,
            category: 'film'
          };
        }
      })
    );

    return moviesWithDetails;
  } catch (error) {
    console.error('Error getting popular movies:', error);
    return [];
  }
};

// TMDB - получение популярных сериалов
export const getPopularTVShows = async (pageSize = 5) => {
  try {
    const response = await fetch(
      `${BACKEND_URL}/external/tmdb/tv/popular?page=1`
    );
    const data = await response.json();

    // Получаем детали для каждого сериала, включая backdrop
    const showsWithDetails = await Promise.all(
      data.results.slice(0, pageSize).map(async (show) => {
        try {
          const detailsResponse = await fetch(
            `${BACKEND_URL}/external/tmdb/tv/${show.id}`
          );
          const details = await detailsResponse.json();

          return {
            id: show.id,
            tmdbId: show.id,
            title: show.name,
            originalTitle: show.original_name,
            overview: show.overview,
            releaseDate: show.first_air_date,
            rating: show.vote_average,
            poster: show.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${show.poster_path}`) : null,
            backdrop: details.backdrop_path
              ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${details.backdrop_path}`)
              : (show.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${show.backdrop_path}`) : null),
            category: 'series'
          };
        } catch {
          return {
            id: show.id,
            tmdbId: show.id,
            title: show.name,
            originalTitle: show.original_name,
            overview: show.overview,
            releaseDate: show.first_air_date,
            rating: show.vote_average,
            poster: show.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${show.poster_path}`) : null,
            backdrop: show.backdrop_path ? proxyImageUrl(`https://image.tmdb.org/t/p/w1280${show.backdrop_path}`) : null,
            category: 'series'
          };
        }
      })
    );

    return showsWithDetails;
  } catch (error) {
    console.error('Error getting popular TV shows:', error);
    return [];
  }
};

// Backend API уже объявлен на 17 строке.

// Получить токен из localStorage
const getAuthToken = () => {
  return localStorage.getItem('polka_token');
};

// Запрос к бэкэнду с авторизацией
export const fetchWithAuth = async (url, options = {}) => {
  if (/^\/(shelf|wishlist)(?:\/-?\d+)?(?:\?|$)/.test(url) || url==='/ratings') {
    const queued=await libraryRequest(url,options,rawFetchWithAuth);
    if(queued.handled)return queued.value;
  }
  const ticket=(options.method || 'GET')==='GET' && /^\/(shelf|wishlist)(?:\?|$)|^\/ratings\/(film|series|game|music|book)\/[^/]+$/.test(url) ? await libraryReadTicket(rawFetchWithAuth):undefined;
  const value=await rawFetchWithAuth(url,options);
  return ticket!==undefined ? libraryResponse(url,value,rawFetchWithAuth,ticket) : value;
};
const rawFetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...analyticsHeaders(),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  const request = () => fetch(`${BACKEND_URL}${url}`, {
    ...options,
    headers
  });
  const method = (options.method || 'GET').toUpperCase();
  const response = await (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)
    ? coalesceWrite(`${token}:${method}:${url}:${options.body || ''}`, request)
    : request());

  if (!response.ok) {
    const body = await response.clone().json().catch(() => ({}));
    const error = new Error(body.error || 'Ошибка запроса');
    error.status = response.status;
    error.payload = body;
    throw error;
  }

  // Each caller consumes its own clone when rapid identical writes are coalesced.
  return response.clone().json();
};

// Регистрация
export const registerUser = async (userData) => {
  try {
    const response = await fetch(`${BACKEND_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка регистрации');
    }

    const data = await response.json();
    localStorage.setItem('polka_token', data.token);
    return data;
  } catch (error) {
    console.error('Ошибка регистрации:', error);
    throw error;
  }
};

// Вход/регистрация через Яндекс
export const yandexLogin = async (code, redirectUri) => {
  try {
    const response = await fetch(`${BACKEND_URL}/auth/yandex`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка авторизации через Яндекс');
    }

    const data = await response.json();
    localStorage.setItem('polka_token', data.token);
    return data;
  } catch (error) {
    console.error('Ошибка Яндекс OAuth:', error);
    throw error;
  }
};

// Вход/регистрация через VK (данные пользователя после SDK exchange)
export const vkLogin = async (payload) => {
  try {
    const response = await fetch(`${BACKEND_URL}/auth/vk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        access_token: payload.access_token,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка авторизации через VK');
    }

    const data = await response.json();
    localStorage.setItem('polka_token', data.token);
    return data;
  } catch (error) {
    console.error('Ошибка VK OAuth:', error);
    throw error;
  }
};

// Установка username после регистрации через Яндекс
export const setUsername = async (username) => {
  return fetchWithAuth('/auth/set-username', {
    method: 'PUT',
    body: JSON.stringify({ username })
  });
};

// Вход
export const loginUser = async (credentials) => {
  try {
    const response = await fetch(`${BACKEND_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Ошибка входа');
    }

    const data = await response.json();
    localStorage.setItem('polka_token', data.token);
    return data;
  } catch (error) {
    console.error('Ошибка входа:', error);
    throw error;
  }
};

// Получить текущего пользователя
export const getCurrentUser = async () => {
  return fetchWithAuth('/auth/me');
};

// Обновить профиль
export const updateProfile = async (profileData) => {
  return fetchWithAuth('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData)
  });
};

// Обновить аватар
export const updateAvatar = async (avatarData) => {
  return fetchWithAuth('/auth/profile', {
    method: 'PUT',
    body: JSON.stringify({ avatar: avatarData })
  });
};

// Получить полку пользователя
export const getUserShelf = async (userId, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = userId ? `/users/${userId}/shelf` : '/shelf';
  return fetchWithAuth(`${url}?${queryString}`);
};

// Добавить на полку
export const addToShelf = async (item) => {
  return fetchWithAuth('/shelf', {
    method: 'POST',
    body: JSON.stringify(item)
  });
};

// Удалить с полки
export const removeFromShelf = async (itemId, options = {}) => {
  return fetchWithAuth(`/shelf/${itemId}`, {
    method: 'DELETE', ...options
  });
};

// Обновить элемент на полке
export const updateShelfItem = async (itemId, updates) => {
  return fetchWithAuth(`/shelf/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
};

// Получить вишлист
export const getWishlist = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithAuth(`/wishlist?${queryString}`);
};

// Добавить в вишлист
export const addToWishlist = async (item) => {
  return fetchWithAuth('/wishlist', {
    method: 'POST',
    body: JSON.stringify(item)
  });
};

// Удалить из вишлиста
export const removeFromWishlist = async (itemId, options = {}) => {
  return fetchWithAuth(`/wishlist/${itemId}`, {
    method: 'DELETE', ...options
  });
};

// Обновить элемент в вишлисте
export const updateWishlistItem = async (itemId, updates) => {
  return fetchWithAuth(`/wishlist/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
};

// Получить друзей
export const getFriends = async () => {
  return fetchWithAuth('/friends');
};

// Получить входящие заявки
export const getIncomingRequests = async () => {
  return fetchWithAuth('/friends/incoming');
};

// Получить исходящие заявки
export const getOutgoingRequests = async () => {
  return fetchWithAuth('/friends/outgoing');
};

// Получить рекомендации
export const getFriendSuggestions = async () => {
  return fetchWithAuth('/friends/suggestions');
};

// Отправить заявку в друзья
export const sendFriendRequest = async (userId) => {
  return fetchWithAuth(`/friends/request/${userId}`, {
    method: 'POST'
  });
};

// Принять заявку в друзья
export const acceptFriendRequest = async (userId) => {
  return fetchWithAuth(`/friends/accept/${userId}`, {
    method: 'POST'
  });
};

// Отклонить заявку в друзья
export const rejectFriendRequest = async (userId) => {
  return fetchWithAuth(`/friends/reject/${userId}`, {
    method: 'POST'
  });
};

// Отменить заявку
export const cancelFriendRequest = async (userId) => {
  return fetchWithAuth(`/friends/cancel/${userId}`, {
    method: 'DELETE'
  });
};

// Удалить из друзей
export const removeFriend = async (userId) => {
  return fetchWithAuth(`/friends/${userId}`, {
    method: 'DELETE'
  });
};

// Проверить статус дружбы
export const getFriendshipStatus = async (userId) => {
  return fetchWithAuth(`/friends/status/${userId}`);
};

// Получить общих друзей
export const getMutualFriends = async (userId) => {
  return fetchWithAuth(`/friends/mutual/${userId}`);
};

// Получить количество общих друзей для нескольких пользователей (batch)
export const getMutualCountsBatch = async (userIds) => {
  return fetchWithAuth('/friends/mutual-counts', {
    method: 'POST',
    body: JSON.stringify({ userIds })
  });
};

// Подписаться на пользователя
export const subscribeToUser = async (userId) => {
  return fetchWithAuth(`/friends/subscribe/${userId}`, {
    method: 'POST'
  });
};

// Отписаться от пользователя
export const unsubscribeFromUser = async (userId) => {
  return fetchWithAuth(`/friends/unsubscribe/${userId}`, {
    method: 'POST'
  });
};

// Получить подписчиков
export const getFollowers = async () => {
  return fetchWithAuth('/friends/followers');
};

// Получить подписки (на кого я подписан)
export const getFollowing = async () => {
  return fetchWithAuth('/friends/following');
};

// Получить друзей пользователя по его ID (для чужого профиля)
export const getUserFriends = async (userId) => {
  return fetchWithAuth(`/friends/user/${userId}`);
};

// Получить подписчиков пользователя по его ID (для чужого профиля)
export const getUserFollowers = async (userId) => {
  return fetchWithAuth(`/friends/user/${userId}/followers`);
};

// Получить подписки пользователя по его ID (на кого он подписан, для чужого профиля)
export const getUserFollowing = async (userId) => {
  return fetchWithAuth(`/friends/user/${userId}/following`);
};

// Оставить в подписчиках (отклонить заявку, но сохранить как подписку)
export const keepAsFollower = async (userId) => {
  return fetchWithAuth(`/friends/keep-as-follower/${userId}`, {
    method: 'POST'
  });
};

// Получить друзей, у которых есть определенный элемент на полке
export const getFriendsWithItem = async (externalId, category) => {
  return fetchWithAuth(`/friends/with-item/${externalId}/${category}`);
};

// Поиск пользователей (для друзей)
export const searchUsersForFriends = async (query) => {
  return fetchWithAuth(`/users/search/${encodeURIComponent(query)}`);
};


// Установить рейтинг
export const setRating = async (externalId, category, rating) => {
  return fetchWithAuth('/ratings', {
    method: 'POST',
    body: JSON.stringify({ external_id: externalId, category, rating })
  });
};

// Получить средний рейтинг элемента (по всем пользователям)
export const getUserItemRating = (externalId, category) =>
  fetchWithAuth(`/ratings/${encodeURIComponent(category)}/${encodeURIComponent(externalId)}`);

export const getAverageItemRating = async (externalId, category) => {
  return fetchWithAuth(`/ratings/average/${category}/${externalId}`);
};

// Получить распределение оценок по звёздам (1-5) для элемента
export const getRatingDistribution = async (externalId, category) => {
  return fetchWithAuth(`/ratings/distribution/${category}/${externalId}`);
};

// Получить комментарии для элемента
export const getComments = async (category, externalId) => {
  return fetchWithAuth(`/ratings/comments/${category}/${externalId}`);
};

// Добавить комментарий
export const addComment = async (category, externalId, text, parentId = null) => {
  return fetchWithAuth(`/ratings/comments`, {
    method: 'POST',
    body: JSON.stringify({ external_id: externalId, category, text, parent_id: parentId })
  });
};

// Обновить комментарий
export const updateComment = async (commentId, text) => {
  return fetchWithAuth(`/comments/${commentId}`, {
    method: 'PUT',
    body: JSON.stringify({ text })
  });
};

// Удалить комментарий
export const deleteComment = async (commentId) => {
  return fetchWithAuth(`/ratings/comments/${commentId}`, {
    method: 'DELETE'
  });
};

// Лайкнуть/убрать лайк комментария
export const likeComment = async (commentId) => {
  return fetchWithAuth(`/ratings/comments/${commentId}/like`, {
    method: 'POST'
  });
};

// Создать уведомление
export const createNotification = async (notificationData) => {
  return fetchWithAuth('/notifications', {
    method: 'POST',
    body: JSON.stringify(notificationData)
  });
};

// Сохранить push-подписку
export const savePushSubscription = async (subscription) => {
  return fetchWithAuth('/push-subscriptions', {
    method: 'POST',
    body: JSON.stringify(subscription)
  });
};

// Удалить push-подписку
export const removePushSubscription = async (endpoint) => {
  return fetchWithAuth('/push-subscriptions', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint })
  });
};

// Получить ленту активности друзей
export const getFeed = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithAuth(`/feed?${queryString}`);
};

export const getFeedHighlights = async () => {
  return fetchWithAuth('/feed-highlights');
};

// Получить блоки для ленты (прорыв пузыря)
export const getFeedBlocks = async () => {
  return fetchWithAuth('/feed/blocks');
};

// Получить выбор редакции (editor's picks) — отдельный эндпоинт
export const getEditorPicks = async () => {
  return fetchWithAuth('/editor-picks');
};

// Проверить членство в фандоме
export const getFandomMembership = async (fandomId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/membership`);
};

// Вступить/покинуть фандом
export const toggleFandomJoin = async (fandomId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/join`, {
    method: 'POST',
  });
};

// Получить конфиг фандома (для получения accent_color и описания)
export const getFandomConfig = async (fandomId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/config`);
};

// Получить публичную ленту
export const getPublicFeed = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithAuth(`/feed/public?${queryString}`);
};

// Скрыть пользователя из рекомендаций ленты (кнопка "Не интересно")
export const dismissFeedUser = async (dismissedUserId) => {
  return fetchWithAuth('/feed/dismiss-user', {
    method: 'POST',
    body: JSON.stringify({ dismissedUserId })
  });
};

// ===== Споры во вкусах =====

// Отметить просмотр/клик карточки "Спор во вкусах" (CTR)
export const recordDisputeClick = async (disputeId) => {
  return fetchWithAuth(`/feed/blocks/${disputeId}/click`, { method: 'POST' });
};

// Добавить/изменить эмоцию-реакцию на карточке
export const addDisputeReaction = async (disputeId, emoji) => {
  return fetchWithAuth(`/feed/blocks/${disputeId}/reaction`, {
    method: 'POST',
    body: JSON.stringify({ emoji })
  });
};

// Скрыть конкретную карточку
export const dismissDispute = async (disputeId) => {
  return fetchWithAuth(`/feed/blocks/${disputeId}/dismiss`, { method: 'POST' });
};

// Скрыть все споры с конкретным другом
export const hideDisputeFriend = async (disputeId) => {
  return fetchWithAuth(`/feed/blocks/${disputeId}/hide-friend`, { method: 'POST' });
};

// ===== «Угадай оценку друга» =====

// Тизер карточки (постер + метаданные, без оценки — защита от подглядывания)
export const getGuessRating = async (guessId) => {
  return fetchWithAuth(`/feed/guess/${guessId}`);
};

// Ответ на диапазон — раскрывает реальную оценку друга
export const answerGuessRating = async (guessId, guessBand) => {
  return fetchWithAuth(`/feed/guess/${guessId}/answer`, {
    method: 'POST',
    body: JSON.stringify({ guess_band: guessBand })
  });
};

// «Пропустить» — скрыть карточку
export const skipGuessRating = async (guessId) => {
  return fetchWithAuth(`/feed/guess/${guessId}/skip`, { method: 'POST' });
};

// Получить аналитику карточек (админ)
export const getRatingDisputesStats = async () => {
  return fetchWithAuth('/admin/rating-disputes');
};

// Получить профиль пользователя по ID
export const getUserProfile = async (userId) => {
  return fetchWithAuth(`/users/${userId}`);
};

// Получить профиль пользователя по username
export const getUserProfileByUsername = async (username) => {
  return fetchWithAuth(`/users/by-username/${encodeURIComponent(username)}`);
};

// Получить комментарии пользователя
export const getUserComments = async (userId, limit = 50) => {
  return fetchWithAuth(`/users/${userId}/comments?limit=${limit}`);
};

// Поиск пользователей
export const searchUsers = async (query) => {
  return fetchWithAuth(`/feed/search/users?q=${encodeURIComponent(query)}`);
};

// Поиск по полкам
export const searchShelves = async (query, category = 'all') => {
  return fetchWithAuth(`/feed/search/shelves?q=${encodeURIComponent(query)}&category=${category}`);
};

// Получить популярные элементы
export const getPopularItems = async (category, limit = 10) => {
  return fetchWithAuth(`/feed/popular/${category}?limit=${limit}`);
};

// Получить рекомендации
export const getRecommendations = async (limit = 10) => {
  return fetchWithAuth(`/feed/recommendations?limit=${limit}`);
};

// Получить тренды
export const getTrends = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return fetchWithAuth(`/feed/trends?${queryString}`);
};

// Лайкнуть активность
export const likeActivity = async (activityId) => {
  return fetchWithAuth(`/feed/${activityId}/like`, {
    method: 'POST'
  });
};

// Убрать лайк с активности
export const unlikeActivity = async (activityId) => {
  return fetchWithAuth(`/feed/${activityId}/like`, {
    method: 'DELETE'
  });
};

// ─── Фандомы ────────────────────────────────────────────────────────────

// Получить список всех фандомов
export const getFandomList = async () => {
  return fetchWithAuth('/fandoms/list');
};

export const getFandomDesignRequest = id => fetchWithAuth(`/fandoms/${encodeURIComponent(id)}/design-request`);
export const requestFandomDesign = (id, details = {}) => fetchWithAuth(`/fandoms/${encodeURIComponent(id)}/design-request`, { method: 'POST',body:JSON.stringify(details) });

export const saveUserFandom = (id, data) => fetchWithAuth(id ? `/fandoms/${encodeURIComponent(id)}` : '/fandoms', {
  method: id ? 'PUT' : 'POST', body: JSON.stringify(data),
});

// Отслеживать / перестать отслеживать фандом
export const toggleFandomTrack = async (fandomId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/track`, { method: 'POST' });
};

// Получить посты фандома
export const getFandomPosts = async (fandomId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/posts?spoilers=1`);
};

// Создать пост в фандоме
export const createFandomPost = async (fandomId, text, media, spoiler = null, announcement = false) => {
  return fetchWithAuth(`/fandoms/${fandomId}/posts`, {
    method: 'POST',
    body: JSON.stringify({ text, media, spoiler, announcement }),
  });
};

// Удалить пост
export const updateFandomDiscussion = async (fandomId, discussionId, title, content) => {
  return fetchWithAuth(`/fandoms/${fandomId}/discussions/${discussionId}`, {
    method: 'PUT',
    body: JSON.stringify({ title, content }),
  });
};

export const deleteFandomDiscussion = async (fandomId, discussionId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/discussions/${discussionId}`, {
    method: 'DELETE',
  });
};

export const deleteFandomPost = async (fandomId, postId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/posts/${postId}`, {
    method: 'DELETE',
  });
};

// Лайкнуть / убрать лайк
export const toggleFandomLike = async (fandomId, postId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/posts/${postId}/like`, {
    method: 'POST',
  });
};

// Получить комментарии к посту
export const getFandomComments = async (fandomId, postId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/posts/${postId}/comments?spoilers=1`);
};

// Добавить комментарий к посту
export const addFandomComment = async (fandomId, postId, text) => {
  return fetchWithAuth(`/fandoms/${fandomId}/posts/${postId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ text })
  });
};

export const getFandomMembers = async (fandomId) => {
  const res = await fetch(`${BACKEND_URL}/fandoms/${fandomId}/members`);
  if (!res.ok) throw new Error('Failed to fetch fandom members');
  return res.json();
};

export const getFandomStats = async () => {
  const res = await fetch(`${BACKEND_URL}/fandoms/stats`);
  if (!res.ok) throw new Error('Failed to fetch fandom stats');
  return res.json();
};

export const getFandomQuotes = async (fandomId) => {
  const res = await fetch(`${BACKEND_URL}/fandoms/${fandomId}/quotes`);
  if (!res.ok) throw new Error('Failed to fetch fandom quotes');
  return res.json();
};

export const createFandomQuote = async (fandomId, quoteData) => {
  return fetchWithAuth(`/fandoms/${fandomId}/quotes`, {
    method: 'POST',
    body: JSON.stringify(quoteData),
  });
};

// Редактировать пост
export const updateFandomPost = async (fandomId, postId, text, media, spoiler, announcement) => {
  return fetchWithAuth(`/fandoms/${fandomId}/posts/${postId}`, {
    method: 'PUT',
    body: JSON.stringify({ text, media, spoiler, announcement }),
  });
};

// Коллекции фандома
export const getFandomCollections = async (fandomId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/collections`);
};

export const addToFandomCollection = async (fandomId, data) => {
  return fetchWithAuth(`/fandoms/${fandomId}/collections`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const removeFromFandomCollection = async (fandomId, itemId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/collections/${itemId}`, {
    method: 'DELETE',
  });
};

export const searchFandomProjects = async (fandomId, query, category) => {
  // Ищем через внешние API по отдельности (надёжнее, чем searchAll)
  const searchFnMap = {
    film: searchMovies, series: searchTVShows, game: searchGames,
    music: searchMusic, book: searchBooks,
  };
  let allItems = [];
  
  if (category && searchFnMap[category]) {
    // Ищем только в выбранной категории
    try {
      const result = await searchFnMap[category](query, 1);
      allItems = (result.results || []).map(item => ({
        itemId: String(item.id || item.tmdbId || item.rawgId || item.igdbId || item.itunesId || item.googleId),
        category: category,
        title: item.title || item.name || '',
        poster: item.poster || item.artwork || null,
        backdrop: item.backdrop || null,
        alreadyInCollection: false,
      }));
    } catch { /* ignore */ }
  } else {
    // Ищем по всем категориям
    const results = await Promise.allSettled([
      searchMovies(query).catch(() => ({ results: [] })),
      searchTVShows(query).catch(() => ({ results: [] })),
      searchGames(query).catch(() => ({ results: [] })),
      searchMusic(query).catch(() => ({ results: [] })),
      searchBooks(query).catch(() => ({ results: [] })),
    ]);
    const catKeys = ['film', 'series', 'game', 'music', 'book'];
    results.forEach((res, idx) => {
      if (res.status === 'fulfilled' && res.value?.results) {
        allItems = allItems.concat(
          (res.value.results || []).map(item => ({
            itemId: String(item.id || item.tmdbId || item.rawgId || item.igdbId || item.itunesId || item.googleId),
            category: catKeys[idx],
            title: item.title || item.name || '',
            poster: item.poster || item.artwork || null,
            backdrop: item.backdrop || null,
            alreadyInCollection: false,
          }))
        );
      }
    });
  }
  
  // Удаляем дубликаты по itemId+category
  const seen = new Set();
  return allItems.filter(item => {
    const key = `${item.itemId}_${item.category}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 20);
};

// Обсуждения фандома
export const getFandomDiscussions = async (fandomId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/discussions`);
};

export const createFandomDiscussion = async (fandomId, title, content) => {
  return fetchWithAuth(`/fandoms/${fandomId}/discussions`, {
    method: 'POST',
    body: JSON.stringify({ title, content }),
  });
};

export const getDiscussionMessages = async (fandomId, discussionId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/discussions/${discussionId}/messages`);
};

export const sendDiscussionMessage = async (fandomId, discussionId, text, parentId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/discussions/${discussionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text, parentId }),
  });
};

export const toggleDiscussionLike = async (fandomId, discussionId, messageId) => {
  return fetchWithAuth(`/fandoms/${fandomId}/discussions/${discussionId}/messages/${messageId}/like`, {
    method: 'POST',
  });
};

// ─── Курированные коллекции (админские) ───────────────────────────────

// Получить список всех курированных коллекций
export const getCuratedCollections = async () => {
  const res = await fetch(`${BACKEND_URL}/collections/curated`);
  if (!res.ok) throw new Error('Failed to fetch curated collections');
  return res.json();
};

// Получить курированную коллекцию по slug
export const getCuratedCollection = async (slug) => {
  const res = await fetch(`${BACKEND_URL}/collections/curated/${slug}`);
  if (!res.ok) throw new Error('Failed to fetch curated collection');
  return res.json();
};

// ─── Достижения ─────────────────────────────────────────────────────────

// Получить все достижения с прогрессом пользователя
export const getAchievements = async () => {
  return fetchWithAuth('/achievements');
};

// Получить статистику достижений
export const getAchievementStats = async () => {
  return fetchWithAuth('/achievements/stats');
};

// Проверить все достижения
export const checkAllAchievements = async () => {
  return fetchWithAuth('/achievements/check', {
    method: 'POST'
  });
};

// Проверить конкретное достижение
export const checkAchievement = async (code) => {
  return fetchWithAuth(`/achievements/check/${code}`, {
    method: 'POST'
  });
};

// Получить достижения пользователя по ID (для чужого профиля)
export const getUserAchievements = async (userId) => {
  return fetchWithAuth(`/achievements/user/${userId}`);
};

// Получить лидерборд по достижениям
export const getAchievementLeaderboard = async (limit = 20) => {
  return fetchWithAuth(`/achievements/leaderboard?limit=${limit}`);
};

// Подтвердить email
export const verifyEmail = async (token) => {
  const response = await fetch(`${BACKEND_URL}/auth/verify-email/${token}`);
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Ошибка подтверждения email');
  }
  return response.json();
};

// Повторно отправить письмо подтверждения
export const resendVerification = async () => {
  return fetchWithAuth('/auth/resend-verification', {
    method: 'POST'
  });
};

// Выйти
export const logout = () => {
  localStorage.removeItem('polka_token');
};

// Получить URL изображения TMDB через бэкенд-прокси (обходит блокировки)
export const getTmdbImageUrl = (path, size = 'w500') => {
  if (!path) return null;
  return `${BACKEND_URL}/external/tmdb/image?path=${encodeURIComponent(path)}&size=${size}`;
};

// ===== TMDB Person API =====

export const getPersonDetails = async (personId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/tmdb/person/${personId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Error getting person details:', error);
    return null;
  }
};

export const getPersonMovieCredits = async (personId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/tmdb/person/${personId}/movie_credits`);
    if (!response.ok) return [];
    const data = await response.json();
    return (data.cast || []).map(item => ({
      id: item.id,
      title: item.title || item.name || '',
      character: item.character || '',
      poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
      releaseDate: item.release_date || '',
      rating: item.vote_average || 0,
      category: 'film'
    })).sort((a, b) => new Date(b.releaseDate || 0) - new Date(a.releaseDate || 0));
  } catch (error) {
    console.error('Error getting person movie credits:', error);
    return [];
  }
};

export const getPersonTVCredits = async (personId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/tmdb/person/${personId}/tv_credits`);
    if (!response.ok) return [];
    const data = await response.json();
    return (data.cast || []).map(item => ({
      id: item.id,
      title: item.name || item.title || '',
      character: item.character || '',
      poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
      releaseDate: item.first_air_date || '',
      rating: item.vote_average || 0,
      category: 'series'
    })).sort((a, b) => new Date(b.releaseDate || 0) - new Date(a.releaseDate || 0));
  } catch (error) {
    console.error('Error getting person TV credits:', error);
    return [];
  }
};

export const getDirectorMovieCredits = async (personId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/tmdb/person/${personId}/movie_credits`);
    if (!response.ok) return [];
    const data = await response.json();
    return (data.crew || [])
      .filter(item => item.job === 'Director')
      .map(item => ({
        id: item.id,
        title: item.title || item.name || '',
        character: 'Режиссёр',
        poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
        releaseDate: item.release_date || '',
        rating: item.vote_average || 0,
        category: 'film'
      }))
      .sort((a, b) => new Date(b.releaseDate || 0) - new Date(a.releaseDate || 0));
  } catch (error) {
    console.error('Error getting director movie credits:', error);
    return [];
  }
};

export const getDirectorTVCredits = async (personId) => {
  try {
    const response = await fetch(`${BACKEND_URL}/external/tmdb/person/${personId}/tv_credits`);
    if (!response.ok) return [];
    const data = await response.json();
    return (data.crew || [])
      .filter(item => item.job === 'Director')
      .map(item => ({
        id: item.id,
        title: item.name || item.title || '',
        character: 'Режиссёр',
        poster: item.poster_path ? proxyImageUrl(`${TMDB_IMAGE_BASE}${item.poster_path}`) : null,
        releaseDate: item.first_air_date || '',
        rating: item.vote_average || 0,
        category: 'series'
      }))
      .sort((a, b) => new Date(b.releaseDate || 0) - new Date(a.releaseDate || 0));
  } catch (error) {
    console.error('Error getting director TV credits:', error);
    return [];
  }
};

export default {
  searchMovies,
  searchTVShows,
  getMovieDetails,
  getTVShowDetails,
  searchGames,
  getGameDetails,
  searchMusic,
  searchBooks,
  getBookDetails,
  searchAll,
  getTrending,
  getRecentGameReleases,
  getRecentMovieReleases,
  getRecentTVShowReleases,
  getPopularMovies,
  getPopularTVShows,
  getMovieRecommendations,
  getTVShowRecommendations,
  getTVShowSimilar,
  getSimilarByCategory,
  getSeasonEpisodes,
  markEpisodeWatched,
  rateEpisode,
  getEpisodeProgress,
  proxyImageUrl,
  yandexLogin,
  setUsername,
  registerUser,
  loginUser,
  getCurrentUser,
  updateProfile,
  updateAvatar,
  getUserShelf,
  addToShelf,
  removeFromShelf,
  updateShelfItem,
  getWishlist,
  addToWishlist,
  removeFromWishlist,
  updateWishlistItem,
  getFriends,
  sendFriendRequest,
  acceptFriendRequest,
  getNotifications,
  setRating,
  addComment,
  getFeed,
  searchUsers,
  logout,
  fetchWithAuth  // Добавляем fetchWithAuth в экспорт
};

export const getCommentPage = (category, externalId, cursor = null, parentId = null) => {
  const query=new URLSearchParams({paged:'1',limit:'12'});
  if(cursor != null)query.set('cursor',String(cursor));
  return fetchWithAuth(`/ratings/comments/${category}/${encodeURIComponent(externalId)}${parentId == null ? '' : `/replies/${parentId}`}?${query}`);
};
